import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { get, post } from '../utils/api';
import { Spinner } from '../components/Shared';

// Decided name-board photos (approved / rejected), grouped by day or by delivery boy.
// Approved photos can be sent back for a redo: the party's location is cleared and the
// driver is asked for a new board photo on the party's next delivery.

const IST = 'Asia/Kolkata';
const pad = n => String(n).padStart(2, '0');
function ymd(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
function dayOf(r) {
  if (!r.approved_at) return 'unknown';
  return new Date(r.approved_at).toLocaleDateString('en-CA', { timeZone: IST });
}
function dayLabel(day) {
  if (day === 'unknown') return 'No date';
  return new Date(day + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}
function when(ts) {
  return ts ? new Date(ts).toLocaleString('en-IN', { timeZone: IST, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
}

export default function LocationHistory({ status }) {
  const today = new Date();
  const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 6);
  const [from, setFrom] = useState(ymd(weekAgo));
  const [to, setTo] = useState(ymd(today));
  const [driverId, setDriverId] = useState('');
  const [groupBy, setGroupBy] = useState('day');
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [photo, setPhoto] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ status });
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      if (driverId) qs.set('driver_id', driverId);
      const d = await get('/api/party-locations/history?' + qs.toString());
      setRows((d && d.submissions) || []);
      setDrivers((d && d.drivers) || []);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load');
    } finally { setLoading(false); }
  }, [status, from, to, driverId]);

  useEffect(() => { load(); }, [load]);

  async function redo(r) {
    const reason = window.prompt('Reject "' + r.party_name + '" and ask ' + r.driver_name + ' for a new board photo?\n\nReason (optional):');
    if (reason === null) return;
    setBusyId(r.id);
    try {
      const res = await post('/api/party-locations/' + r.id + '/redo', { reason });
      if (res && res.location_cleared === false) {
        window.alert('Marked rejected. A newer location was already saved for this party, so the saved location was left as it is.');
      }
      await load();
    } catch (e) { window.alert(e.message || 'Failed'); }
    finally { setBusyId(null); }
  }

  const groups = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const day = dayOf(r);
      const key = groupBy === 'day' ? day : r.driver_id;
      if (!m.has(key)) m.set(key, { key, title: groupBy === 'day' ? dayLabel(day) : r.driver_name, items: [] });
      m.get(key).items.push(r);
    }
    const list = Array.from(m.values());
    if (groupBy === 'boy') list.sort((a, b) => String(a.title).localeCompare(String(b.title)));
    if (groupBy === 'day') {
      list.forEach(g => g.items.sort((a, b) => String(a.driver_name).localeCompare(String(b.driver_name))));
    }
    return list;
  }, [rows, groupBy]);

  const inputCls = 'border rounded-lg px-2 py-1.5 text-sm bg-white';

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-xs text-gray-500">From<br />
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inputCls} /></label>
        <label className="text-xs text-gray-500">To<br />
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inputCls} /></label>
        <label className="text-xs text-gray-500">Delivery boy<br />
          <select value={driverId} onChange={e => setDriverId(e.target.value)} className={inputCls}>
            <option value="">All</option>
            {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select></label>
        <div className="text-xs text-gray-500">Group by<br />
          <div className="inline-flex rounded-lg border overflow-hidden">
            {[['day', 'Day'], ['boy', 'Boy']].map(([k, label]) => (
              <button key={k} onClick={() => setGroupBy(k)}
                className={'px-3 py-1.5 text-sm ' + (groupBy === k ? 'bg-gray-800 text-white' : 'bg-white text-gray-700')}>{label}</button>
            ))}
          </div>
        </div>
        <div className="text-sm text-gray-500 ml-auto">{rows.length} {status}{rows.length >= 500 ? ' (showing latest 500)' : ''}</div>
      </div>

      {error && <div className="p-3 mb-4 rounded bg-red-50 text-red-700 text-sm">{error}</div>}
      {loading && <Spinner />}
      {!loading && !error && rows.length === 0 && <div className="p-6 text-center text-gray-500">No {status} photos for this selection.</div>}

      {!loading && groups.map(g => (
        <div key={g.key} className="mb-5">
          <div className="sticky top-0 bg-gray-100 rounded-lg px-3 py-1.5 mb-2 text-sm font-semibold text-gray-700">
            {g.title} <span className="font-normal text-gray-500">({g.items.length})</span>
          </div>
          {g.items.map(r => {
            const acc = r.accuracy_metres != null ? Number(r.accuracy_metres) : null;
            const addr = [r.area_name, r.city].filter(Boolean).join(', ');
            return (
              <div key={r.id} className="border rounded-xl p-3 mb-2 bg-white flex flex-col sm:flex-row gap-3">
                <img src={r.photo_url} alt="Name board" onClick={() => setPhoto(r.photo_url)}
                  className="w-full sm:w-40 h-28 object-cover rounded-lg cursor-pointer" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{r.party_name}</div>
                  <div className="text-sm text-gray-600">{addr}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Photo by {r.driver_name} - taken {when(r.created_at)} - {r.status} {when(r.approved_at)}{r.decided_by_name ? ' by ' + r.decided_by_name : ''}
                  </div>
                  <div className="text-xs text-gray-500">GPS accuracy: {acc === null ? 'unknown' : acc + ' m'}</div>
                  {r.status === 'approved' && (
                    <div className={'inline-block mt-1 px-2 py-0.5 rounded text-xs ' + (r.is_live ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600')}>
                      {r.is_live ? 'This is the saved location' : 'Replaced by a newer location'}
                    </div>
                  )}
                  {r.status === 'rejected' && r.reject_reason && <div className="text-xs text-red-700 mt-1">Reason: {r.reject_reason}</div>}
                </div>
                {r.status === 'approved' && (
                  <div className="flex sm:flex-col justify-end">
                    <button disabled={busyId === r.id} onClick={() => redo(r)}
                      className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-50">Reject and redo</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {photo && (
        <div onClick={() => setPhoto(null)} className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <img src={photo} alt="Name board" className="max-w-full max-h-full" />
        </div>
      )}
    </div>
  );
}
